import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Shield, Sparkles } from 'lucide-react';
import { Language } from '../App';
interface SubscriptionSupportProps {
  language: Language;
}
export function SubscriptionSupport({
  language
}: SubscriptionSupportProps) {
  const isPashto = language === 'pashto';
  return <section className="py-10 md:py-14 px-6 bg-gradient-to-b from-white via-pasoon-green/20 to-white overflow-hidden" data-id="element-258">
      <div className="max-w-5xl mx-auto" data-id="element-259">
        <AnimatePresence mode="wait" data-id="element-260">
          <motion.div key={language} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-261">
            {/* Closing Statement */}
            <div className="p-6 md:p-8 bg-pasoon-dark rounded-2xl mb-8 md:mb-10" data-id="element-262">
              <p className={`text-xl md:text-2xl font-bold text-white text-center ${isPashto ? 'font-pashto leading-loose' : 'font-serif'}`} data-id="element-263">
                {isPashto ? 'پاڅون یوازې یوه مجله نه ده—دا د یو ژوندي تمدن ساتونکی غږ دی' : 'Pasoon is not just a magazine — it is the guardian voice of a living civilisation.'}
              </p>
            </div>

            {/* Support CTA */}
            <div className="mb-10 md:mb-14 text-center" data-id="element-264">
              <a href="https://www.paypal.com/ncp/payment/VVWBMJDVRC7W4" target="_blank" rel="noopener noreferrer" className={`inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-pasoon-red text-white rounded-full font-medium text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-265">
                <Heart className="w-5 h-5" data-id="element-266" />
                {isPashto ? 'د ماموریت سره مرسته وکړئ' : 'Support the Mission'}
              </a>
            </div>

            {/* Section Header */}
            <div className="text-center mb-8 md:mb-10" data-id="element-267">
              <h2 className={`text-3xl md:text-5xl font-bold text-pasoon-dark mb-4 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-268">
                {isPashto ? 'پاڅون ژوندی وساتئ' : 'Keep Pasoon Alive'}
              </h2>
              <p className={`text-gray-500 text-base md:text-lg max-w-xl mx-auto ${isPashto ? 'font-pashto' : ''}`} data-id="element-269">
                {isPashto ? 'ستاسو مرسته ډاډ ورکوي چې پښتو ادب د نړۍ هر ګوټ ته ورسیږي' : 'Your support ensures Pashto literature reaches every corner of the world'}
              </p>
            </div>

            {/* Single Support Card — Centred */}
            <div className="max-w-xl mx-auto" data-id="element-270">
              <div className="relative bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 p-8 md:p-10 flex flex-col overflow-hidden" data-id="element-271">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-pasoon-muted" data-id="element-272"></div>

                <div className="flex items-center gap-3 mb-6" data-id="element-273">
                  <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center" data-id="element-274">
                    <Heart className="w-5 h-5 text-pasoon-muted" data-id="element-275" />
                  </div>
                  <h3 className={`text-xl md:text-2xl font-bold text-pasoon-dark ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-276">
                    {isPashto ? 'د پښتو ملاتړ وکړئ' : 'Support Pashto'}
                  </h3>
                </div>

                <p className={`text-gray-500 mb-6 ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-277">
                  {isPashto ? 'زموږ کار د پښتو ودانول او ساتنه ده. که تاسو مورنۍ ژبه سره مینه لرئ، نو مهرباني وکړئ زموږ کار ته ملاتړ وکړئ.' : 'Our work is to promote and preserve Pashto. If you love the Mother Language, then please support our work.'}
                </p>

                {/* Sparkle note */}
                <div className="flex items-start gap-3 bg-green-50/80 rounded-xl p-4 mb-8" data-id="element-278">
                  <Sparkles className="w-5 h-5 text-pasoon-muted flex-shrink-0 mt-0.5" data-id="element-279" />
                  <p className={`text-sm text-pasoon-muted ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-280">
                    {isPashto ? 'هره مرسته مستقیماً د پاڅون راتلونکې ګڼه او پښتو تمویلوي.' : 'Every contribution directly funds the next issue of Pasoon and Pashto.'}
                  </p>
                </div>

                {/* CTA */}
                <a href="https://www.paypal.com/ncp/payment/VVWBMJDVRC7W4" target="_blank" rel="noopener noreferrer" className={`w-full flex items-center justify-center gap-2 px-8 py-4 bg-pasoon-muted text-white rounded-full font-medium text-lg hover:bg-green-800 transition-all shadow-lg shadow-green-900/20 hover:shadow-green-900/30 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-281">
                  <Heart className="w-5 h-5" data-id="element-282" />
                  {isPashto ? 'د پښتو ملاتړ وکړئ' : 'Support Pashto'}
                </a>

                {/* Trust */}
                <div className={`flex items-center justify-center gap-1.5 mt-4 text-gray-400 text-xs ${isPashto ? 'font-pashto' : ''}`} data-id="element-283">
                  <Shield className="w-3.5 h-3.5" data-id="element-284" />
                  {isPashto ? 'خوندي PayPal تادیه' : 'Secure PayPal Payment'}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}